import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useEffect, useCallback } from 'react';

import { useQueryParam, StringParam } from 'routing/QueryParams';
import SecurityRoutes from 'security-app/routing/Routes';
import { useGetInvestigationDetails } from 'security-app/hooks/useInvestigationsAPI';
import { useInvestigationDrawer } from 'security-app/components/Investigations/contexts';
import { Icon, Spinner, IfPermitted } from 'components/common';
import { Button, Row, Col, SegmentedControl, MenuItem, DropdownButton } from 'components/bootstrap';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import type { InvestigationsDetailsAPIType } from 'security-app/hooks/api/investigationsAPI.types';
import type { SecurityEventAPIType } from 'security-app/hooks/api/securityEventsAPI.types';
import AlertsModals from 'security-app/components/SecurityEvents/Alerts/AlertsModals';
import { ModalProvider } from 'common/contexts';
import AIReportButton from 'security-app/components/Investigations/AIReport/AIReportButton';
import useIsInvestigationAIReportEnabled from 'security-app/components/Investigations/AIReport/hooks/useIsInvestigationAIReportEnabled';

import DateStat from './DateStat';
import OverviewSegment from './OverviewSegment';
import EditInvestigation from './EditInvestigation';
import MainDetails from './MainDetails';
import TimelineSegment from './TimelineSegment';

const OVERVIEW_SEGMENT = 'overview' as const;
const TIMELINE_SEGMENT = 'timeline' as const;

const DETAILS_SEGMENTS = [
  {
    value: OVERVIEW_SEGMENT,
    label: 'Overview',
  },
  {
    value: TIMELINE_SEGMENT,
    label: 'Timeline',
  },
];

type DetailsSegments = 'overview' | 'timeline';

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`;

const Header = styled.div<{ $inModal?: boolean }>(
  ({ $inModal }) => css`
    display: flex;
    justify-content: space-between;
    padding-top: 30px;
    margin-bottom: 20px;
    gap: 15px;
    margin-left: ${$inModal ? 'none':  '-15px'};
    margin-right: ${$inModal ? 'none' : '-15px'};
  `
);

const LeftCol = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const RightCol = styled.div`
  display: flex;
  gap: 30px;
`;

const StyledRow = styled(Row) <{ $inModal?: boolean; }>`
  ${({ $inModal }) =>
    $inModal &&
    css`
      margin-left: 0;
      margin-right: 0;
    `}
`;

const MainDetailsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const MainDetailsLeft = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const SegmentContainer = styled(Row) <{ $inModal?: boolean; }>`
  flex: 1;

  ${({ $inModal }) =>
    $inModal &&
    css`
      margin-left: 0;
      margin-right: 0;
    `}
`;

const FullHeightCol = styled(Col)`
  height: 100%;
`;

const isValidSegment = (segment: string): segment is DetailsSegments =>
  segment === OVERVIEW_SEGMENT || segment === TIMELINE_SEGMENT;

const useCurrentSegment = (): [DetailsSegments, (segment: DetailsSegments) => void] => {
  const [currentSegment, setCurrentSegment] = useQueryParam('segment', StringParam);

  const _setCurrentSegment = useCallback(
    (newSegment: DetailsSegments) => {
      setCurrentSegment(newSegment);
    },
    [setCurrentSegment],
  );

  return [isValidSegment(currentSegment) ? currentSegment : OVERVIEW_SEGMENT, _setCurrentSegment];
};

const InvestigationDetails = ({ investigation, inModal = false }: { investigation: InvestigationsDetailsAPIType, inModal?: boolean }) => {
  const sendTelemetry = useSendTelemetry();
  const isAIReportFeatureEnabled = useIsInvestigationAIReportEnabled();
  const { setSelectedInvestigationId } = useInvestigationDrawer();
  const navigate = useNavigate();
  const [currentSegment, setCurrentSegment] = useCurrentSegment();
  const [showEditModal, setShowEditModal] = React.useState<boolean>(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => setSelectedInvestigationId(undefined), []);

  const toggleEditModal = useCallback(() => setShowEditModal((cur) => !cur), []);

  const _setCurrentSegment = useCallback(
    (newSegment: DetailsSegments) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_FILTER_UPDATED, {
        app_pathname: 'security',
        app_section: 'investigation',
        event_details: {
          section: newSegment,
        },
      });

      setCurrentSegment(newSegment);
    },
    [sendTelemetry, setCurrentSegment],
  );

  return (
    <Container>
      <Header $inModal={inModal}>
        <LeftCol>
          {!inModal && <Button onClick={() => navigate(SecurityRoutes.INVESTIGATIONS.LIST)}>
            <Icon name="arrow_left_alt" size="sm" /> Back
          </Button>}

          <h1>{investigation.name}</h1>

          <IfPermitted permissions="investigations:edit">
            <DropdownButton title={<Icon name="more_horiz" />} id="investigation-actions" noCaret bsSize="xs">
              <MenuItem onClick={toggleEditModal}>Edit</MenuItem>
            </DropdownButton>
          </IfPermitted>
        </LeftCol>
        <RightCol>
          <DateStat date={investigation.created_at} description="Days Open" />
          <DateStat date={investigation.updated_at} description="Latest Activity" />
        </RightCol>
      </Header>

      <StyledRow className="content no-bm" $inModal={inModal}>
        <Col xs={12}>
          <MainDetailsRow>
            <MainDetailsLeft>
              <SegmentedControl<DetailsSegments>
                data={DETAILS_SEGMENTS}
                value={currentSegment}
                onChange={_setCurrentSegment}
              />
              {isAIReportFeatureEnabled && (
                <AIReportButton investigation={investigation} logsCount={investigation.logs.length} />
              )}
            </MainDetailsLeft>
            <MainDetails
              priority={investigation.priority}
              status={investigation.status}
              assignedTo={investigation.assigned_to}
            />
          </MainDetailsRow>
        </Col>
      </StyledRow>
      <SegmentContainer className="content" $inModal={inModal}>
        <FullHeightCol xs={12}>
          {currentSegment === OVERVIEW_SEGMENT && <OverviewSegment investigation={investigation} />}
          {currentSegment === TIMELINE_SEGMENT && (
            <TimelineSegment
              events={investigation.events}
              logs={investigation.logs}
              investigationId={investigation.id}
            />
          )}
        </FullHeightCol>
      </SegmentContainer>
      {showEditModal && <EditInvestigation investigation={investigation} onClose={toggleEditModal} show />}
    </Container>
  );
};

const DetailsWrapper = ({ investigation = undefined, inModal = false }: { investigation?: InvestigationsDetailsAPIType, inModal?: boolean }) => {
  const { id: investigationsId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { loadingInvestigationDetails, fetchError, currentInvestigation } =
    useGetInvestigationDetails(investigationsId);

  if (loadingInvestigationDetails) {
    return <Spinner text="Loading investigation details..." />;
  }

  if (fetchError) {
    navigate(SecurityRoutes.INVESTIGATIONS.LIST);
  }

  if (!fetchError) {
    return (
      <ModalProvider<any, SecurityEventAPIType>>
        <AlertsModals />
        <InvestigationDetails investigation={investigation || currentInvestigation} inModal={inModal} />
      </ModalProvider>
    );
  }

  return null;
};

export default DetailsWrapper;
